import { FormAnexoCertificado } from "@/contexts/laboratorio/analiseLaboratorial/components/FormAnexoCertificado/FormAnexoCertificado";
import { FormDadosGeraisCertificado } from "@/contexts/laboratorio/analiseLaboratorial/components/FormDadosGeraisCertificado/FormDadosGeraisCertificado";
import { FormDadosRequisicaoAnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/components/FormDadosRequisicaoAnaliseLaboratorial/FormDadosRequisicaoAnaliseLaboratorial";
import { FormTabelaEnsaios } from "@/contexts/laboratorio/analiseLaboratorial/components/FormTabelaEnsaios/FormTabelaEnsaios";
import { useAnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/hooks/useAnaliseLaboratorial";
import { AnaliseLaboratorial } from "@/contexts/laboratorio/analiseLaboratorial/model/AnaliseLaboratorial";
import { CertificadoAnaliseFormValues } from "@/contexts/laboratorio/analiseLaboratorial/model/CertificadoAnaliseFormValues";
import { useLaboratorioAnalise } from "@/contexts/laboratorio/laboratorioAnalise";
import { PageLoader } from "@/shared/components/PageLoader/PageLoader";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Col,
  Divider,
  Form,
  Grid,
  Result,
  Row,
  Space,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

export function NovoCertificadoAnaliseLaboratorial() {
  const { isOnline } = useNetworkStatus();
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { notification, message } = App.useApp();
  const { requisicaoId } = useParams<{ requisicaoId: string }>();
  const { getAnaliseLaboratorialByIdApi, submitCertificadoApi } =
    useAnaliseLaboratorial();
  const { listaLaboratorios } = useLaboratorioAnalise();
  const [form] = Form.useForm<CertificadoAnaliseFormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: requisicao,
    isLoading: isLoadingRequisicao,
    error,
  } = useQuery({
    queryKey: ["requisicao", requisicaoId],
    enabled: isOnline && !!requisicaoId,
    queryFn: () => getAnaliseLaboratorialByIdApi(Number(requisicaoId)),
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleFinish = async (values: CertificadoAnaliseFormValues) => {
    const laboratorioSelecionado = listaLaboratorios.find(
      (lab) => lab.id === values.laboratorio.id
    );

    const certificadoRequest: Partial<AnaliseLaboratorial> = {
      numeroRegistroLaboratorio: values.numRegistroLaboratorio,
      observacaoRecebimento: values.observacaoRecebimento,
      dataRecebimento:
        values.dataRecebimento &&
        values.dataRecebimento.format("YYYY-MM-DD[T]HH:mm:ss"),
      itens: values.ensaiosCertificado.map((ensaio) => ({
        ensaio: ensaio.ensaio,
        conforme: ensaio.conforme || false,
        naoRealizada: ensaio.naoRealizada || false,
        resultado: ensaio.resultado?.toString() || "",
      })),
      amostraImpropria: values.amostraImpropria || false,
      laboratorio: laboratorioSelecionado,
      resultadoUmidade: values.resultadoUmidade,
    };

    const formData = new FormData();
    formData.append(
      "certificadoRequest",
      new Blob([JSON.stringify(certificadoRequest)], {
        type: "application/json",
      })
    );

    if (values.anexo.length > 0) {
      formData.append("anexo", values.anexo[0].originFileObj!);
    }

    try {
      message.loading({
        content: "Salvando certificado...",
        key: "submit",
        duration: 0,
      });

      setIsSubmitting(true);
      await submitCertificadoApi(Number(requisicaoId), formData);

      setIsSubmitting(false);
      message.destroy("submit");
      notification.success({
        message: "Certificado salvo com sucesso",
      });
      navigate(-1);
    } catch (error) {
      console.error("Erro ao salvar o certificado:", error);
      message.destroy("submit");
      setIsSubmitting(false);
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possível salvar o certificado",
        description: messageError,
      });
    }
  };

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Novo Certificado de Análise Laboratorial";

    if (requisicao) {
      form.setFieldsValue({
        resultadoUmidade: requisicao.resultadoUmidade,
        laboratorio: {
          id: requisicao.laboratorio?.id,
        },
        ensaiosCertificado: requisicao.itens.map((item, ind) => ({
          key: ind,
          ensaio: item.ensaio,
          conforme: item.conforme,
          naoRealizada: item.naoRealizada,
          resultado: item.resultado ? Number(item.resultado) : undefined,
        })),
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requisicao]);

  if (isLoadingRequisicao) {
    return <PageLoader />;
  }

  if (!isOnline) {
    return <Result title="Sem conexão com a internet" status={"error"} />;
  }

  if (error) {
    return <Result title="Erro ao carregar a requisição" status={"error"} />;
  }

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} align="center">
        <Typography.Title
          level={screens.xs ? 4 : 3}
          style={{ textAlign: "center" }}
        >
          Certificado de Analise
        </Typography.Title>
      </Space>

      <Divider />

      <FormDadosRequisicaoAnaliseLaboratorial requisicao={requisicao} />

      <Divider orientation="left"></Divider>

      <Form
        layout="vertical"
        form={form}
        onFinish={handleFinish}
        scrollToFirstError
        onFinishFailed={(errorInfo) => {
          console.log("Failed:", errorInfo);

          notification.error({
            message: "Verifique os campos obrigatórios",
            description: errorInfo.errorFields
              .map((err) => err.name)
              .join(", "),
            key: "form-error",
            duration: 5,
          });
        }}
      >
        <FormDadosGeraisCertificado />

        <FormAnexoCertificado form={form} />

        <FormTabelaEnsaios
          form={form}
          produto={requisicao?.produto}
          estabelecimentoId={requisicao!.estabelecimento!.id}
          naturezaAnalise={requisicao!.natureza}
          isAgua={requisicao?.agua}
          exigeResultadoUmidade={
            requisicao!.produto?.produto?.exigeResultadoUmidade &&
            requisicao?.natureza === "MICROBIOLOGICA"
          }
        />

        <Divider />

        <Row justify={"center"} gutter={[10, 10]}>
          <Col>
            <Button
              htmlType="button"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              disabled={isSubmitting}
              style={{ width: 200 }}
            >
              Voltar
            </Button>
          </Col>

          <Col>
            <Button
              type="primary"
              htmlType="submit"
              style={{ width: 200 }}
              icon={<SaveOutlined />}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Salvar
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}
